import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  rectIntersection,
  useDndContext,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import {
  BoltIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  FolderIcon,
  PaperClipIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  ChevronRightIcon,
  Link2Icon,
  MagnifyingGlassIcon,
  Pencil2Icon,
} from "@radix-ui/react-icons";
import { ObjectID } from "bson";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  forwardRef,
  HTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FileResource2Data,
  FolderData,
  FormResource2Data,
  LinkResource2Data,
  Resource2Data,
  ResourceData,
  ResourceWithFoldersData,
  useCreatePhaseLinkResource,
  useCreateResourceFolder,
  useDeleteFolder,
  useDeleteResource,
  useGetResourcesWithFolders,
  useMoveItemsToFolder,
  useUpdateFolder,
  useUpdatePhaseResource,
} from "src/api/Services/SharedSpace/files";
import { useUploadPhaseFile } from "src/api/Services/SharedSpace/phases";
import { TaskData, useGetPhaseTasks } from "src/api/Services/Tasks/tasks";
import {
  useGetUploadLinksForFiles,
  useUploadFileToS3,
} from "src/api/files/files";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import { Count } from "src/components/ui/count";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { Loading } from "src/components/ui/loading";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "src/components/ui/select";
import { TextInput } from "src/components/ui/text-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useRealmApp } from "src/store/RealmApp";
import { useResourcesContext } from "./resources-context-provider";

type Props = {
  phaseId: string;
};

export default function PhaseResources({ phaseId }: Props) {
  const resourcesCtx = useResourcesContext();
  const [activeId, setActiveId] = useState<string | null>(null);
  const { data: resourcesWithFolders } = useGetResourcesWithFolders(phaseId);
  // console.log(resourcesWithFolders);
  const [search, setSearch] = useState("");
  // const resourcesToShow = useMemo(() => {
  //   if (!resourcesWithFolders) return [];
  //   if (resourcesCtx.state.isExternal) {
  //     return resourcesWithFolders.filter((r) =>
  //       r.type === "form" ? r.task.isVisible : r.isVisible
  //     );
  //   } else {
  //     return resourcesWithFolders;
  //   }
  // }, [resourcesWithFolders]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 250, // Delay before drag starts
      tolerance: 100, // Movement allowed before drag cancels
    },
  });

  const sensors = useSensors(mouseSensor);
  const moveItemsToFolderMutation = useMoveItemsToFolder(phaseId);
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const { parent: sourceParent, item: draggedItem } = findItemAndParent(
        resourcesWithFolders ?? [],
        active.id.toString()
      );
      const { item: destinationItem } = findItemAndParent(
        resourcesWithFolders ?? [],
        over.id.toString()
      );

      if (draggedItem && over.id === "artifacts") {
        moveItemsToFolderMutation.mutate({
          items: [{ _id: draggedItem._id, type: draggedItem.type }],
          parentFolderId: null,
        });
      } else if (
        draggedItem &&
        destinationItem &&
        destinationItem.type === "folder"
      ) {
        // console.log({
        //   items: [{ _id: draggedItem._id, type: draggedItem.type }],
        //   parentFolderId: destinationItem._id,
        // });
        moveItemsToFolderMutation.mutate({
          items: [{ _id: draggedItem._id, type: draggedItem.type }],
          parentFolderId: destinationItem._id,
        });
        // const sourceChildren = sourceParent.children;
        // sourceParent.children = sourceChildren.filter(
        //   (item) => item._id.toString() !== draggedItem._id.toString()
        // );

        // (destinationItem as FolderData).children.push(draggedItem);

        // console.log(sourceParent, draggedItem, destinationItem);
        // setData([...data]);
      }
    }

    setActiveId(null);
  };
  // const viewableResources =
  //   (resourcesCtx.state.isExternal
  //     ? resources?.filter((r) =>
  //         r.type === "form" ? r.task.isVisible : r.isVisible
  //       )
  //     : resources) ?? [];

  // const filteredResources = resourcesToShow.filter((r) =>
  //   r.name.toLowerCase().includes(search.toLowerCase())
  // );
  const [selectedResource, setSelectedResource] = useState<ResourceData | null>(
    null
  );
  const { data: tasks } = useGetPhaseTasks(phaseId);
  const [tree, setTree] = useState<{ id: string; name: string }[]>([
    { id: "artifacts", name: "All Artifacts" },
  ]);

  const resourcesToShow = useMemo<ResourceWithFoldersData>(() => {
    if (!resourcesWithFolders) return [];
    let current: ResourceWithFoldersData = resourcesWithFolders;
    if (resourcesCtx.state.isExternal) {
      current = current.filter((r) =>
        r.type === "folder"
          ? r.isVisible
          : r.type === "file"
          ? r.isVisible
          : r.type === "link"
          ? r.isVisible
          : r.type === "form"
          ? r.task.isVisible
          : false
      );
    }
    if (tree.length === 1) {
      return current;
    }
    for (const node of tree.slice(1)) {
      const folder = current.find(
        (item) => item.type === "folder" && item._id === node.id
      ) as FolderData | undefined;

      if (!folder) {
        return [];
      }
      current = folder.children;
    }
    return current;
  }, [resourcesWithFolders, tree]);

  const filteredResourcesToShow = useMemo(() => {
    return resourcesToShow.filter((r) =>
      r.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [resourcesToShow, search]);

  useEffect(() => {
    setSearch("");
  }, [tree.length]);

  // const getCurrentTree = (): { id?: string; name: string }[] => {
  //   let current: { id?: string; name: string }[] = [{ name: "Artifacts" }];
  //   let currentResources: ResourceWithFoldersData = resourcesWithFolders ?? [];

  //   for (const folderId of tree) {
  //     const folder = currentResources.find(
  //       (item) => item.type === "folder" && item._id === folderId
  //     ) as FolderData | undefined;

  //     if (!folder) {
  //       return [];
  //     }

  //     current.push({ id: folder._id, name: folder.name });
  //     currentResources = folder.children;
  //   }
  //   return current;
  // };
  // console.log(getCurrentTree());
  const [initialCursorOffset, setInitialCursorOffset] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  const fixCursorSnapOffset: CollisionDetection = (args) => {
    // Bail out if keyboard activated
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      // The collision rectangle is broken when using snapCenterToCursor. Reset
      // the collision rectangle based on pointer location and overlay size.
      collisionRect: {
        width,
        height,
        bottom: y + height / 2,
        left: x - width / 2,
        right: x + width / 2,
        top: y - height / 2,
      },
    };
    return rectIntersection(updated);
  };

  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <DndContext
      collisionDetection={fixCursorSnapOffset}
      onDragStart={(event) => {
        setActiveId(event.active.id.toString());
        // Access draggableNodes from the DndContext
        // const { draggableNodes } = useDndContext();
        // const draggableNode = draggableNodes.get(event.active.id)?.node.current;

        // if (draggableNode && event.activatorEvent instanceof MouseEvent) {
        //   const rect = draggableNode.getBoundingClientRect();

        //   const offsetX = event.activatorEvent.pageX - (rect.left + rect.width / 2);
        //   const offsetY = event.activatorEvent.pageY - (rect.top + rect.height / 2);

        //   setInitialCursorOffset({ x: offsetX, y: offsetY });
        // }
      }}
      onDragEnd={handleDragEnd}
      sensors={sensors}
      // collisionDetection={rectIntersection}
    >
      {!!resourcesWithFolders ? (
        <>
          {/* <div className="flex h-14 items-center border-b border-gray-200/70 px-8 text-sm">
                <div className="flex flex-1 items-center space-x-8 text-gray-500">
                  <div>Resources</div>
                </div>
              </div> */}
          {resourcesWithFolders.length > 0 && (
            <div className="flex h-16 items-center justify-between border-b border-gray-200/70 px-8">
              <div className="flex max-w-[600px] flex-1 items-center space-x-0.5">
                {tree.map((t, index) => (
                  <>
                    {index > 0 && (
                      <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                    )}
                    {index !== tree.length - 1 ? (
                      <DroppableFolder
                        hoverColor="#f3f4f6"
                        className="-ml-1.5 rounded-md px-1.5 py-0.5"
                        folderId={t.id}
                      >
                        <div
                          key={t.id}
                          className="cursor-pointer text-sm text-gray-800"
                          onClick={() => {
                            if (t.id) {
                              const index = tree.indexOf(t);
                              if (index !== -1) {
                                setTree(tree.slice(0, index + 1));
                              }
                            }
                          }}
                        >
                          {t.name}
                        </div>
                      </DroppableFolder>
                    ) : (
                      <div
                        key={t.id}
                        className="cursor-default pl-1.5 text-sm text-gray-800 first:pl-0"
                      >
                        {t.name}
                      </div>
                    )}
                  </>
                ))}
                {tree.length > 1 && (
                  <div className="flex items-center pl-1.5">
                    <FolderDropdownMenuShell
                      folderId={tree[tree.length - 1].id}
                      resourcesWithFolders={resourcesWithFolders ?? []}
                      phaseId={phaseId}
                      onDelete={() => {
                        setTree(tree.slice(0, -1));
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <TextInput
                    className="h-8 w-72 pl-8"
                    placeholder="Search.."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <NewFolderButton
                  phaseId={phaseId}
                  parentFolder={
                    tree.length > 1 ? tree[tree.length - 1].id : null
                  }
                />
                <AddResourceButton phaseId={phaseId} tree={tree} />
                {/* {resourcesCtx.state.isExternal ?  */}
              </div>
            </div>
          )}
          {resourcesToShow.length > 0 ? (
            <>
              <div className="border-b border-gray-200/70 text-sm">
                <div className="flex h-11 justify-between gap-6 bg-gray-50 px-8 text-gray-500">
                  <div className="flex flex-1 items-center space-x-8">
                    <div className="min-w-[160px] flex-1">Name</div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {!resourcesCtx.state.isExternal && (
                      <div className="flex w-32 flex-col items-center">
                        Visibility
                      </div>
                    )}
                    <div className="w-40">Created By</div>
                    <div className="w-32">Created At</div>
                    <div className="w-16" />
                    {/* <div className="w-20">Updated At</div> */}
                  </div>
                </div>
              </div>
              {filteredResourcesToShow.length > 0 ? (
                <div className="overflow-auto">
                  {filteredResourcesToShow.map((r) => {
                    return r.type === "form" ? (
                      <FormResourceRow
                        key={r._id}
                        resource={r}
                        isExternal={resourcesCtx.state.isExternal}
                      />
                    ) : r.type === "file" ? (
                      <FileResourceRow
                        phaseTasks={tasks ?? []}
                        key={r._id}
                        resource={r}
                        isExternal={resourcesCtx.state.isExternal}
                      />
                    ) : r.type === "link" ? (
                      <LinkResourceRow
                        key={r._id}
                        resource={r}
                        isExternal={resourcesCtx.state.isExternal}
                      />
                    ) : (
                      <DroppableFolder
                        hoverColor={"#f9fafb"}
                        key={r._id}
                        folderId={r._id}
                      >
                        <FolderResourceRow
                          phaseId={phaseId}
                          setTree={setTree}
                          tree={tree}
                          key={r._id}
                          resource={r}
                          isExternal={resourcesCtx.state.isExternal}
                        />
                      </DroppableFolder>
                    );
                  })}
                  {/* {filteredResources.length === 0 && (
                <div className="flex h-12 items-center border-b border-gray-100 px-8 text-sm text-gray-400">
                  <div>No resource found..</div>
                </div>
              )} */}
                </div>
              ) : (
                <div className="flex h-20 items-center justify-center px-8 text-gray-700">
                  No artifacts match your search
                </div>
              )}
            </>
          ) : (
            <div className="w-full overflow-auto">
              <div className="mx-auto mt-[10vh] w-fit">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-lg font-medium">No Artifacts</div>
                    <div className="pt-4 font-light leading-relaxed text-gray-700">
                      Begin by adding a file or link. If you want to group
                      artifacts, you can create a folder first.
                    </div>
                    <div className="flex space-x-2.5 pt-7">
                      <AddResourceButton phaseId={phaseId} tree={tree} />
                      <NewFolderButton
                        phaseId={phaseId}
                        parentFolder={
                          tree.length > 1 ? tree[tree.length - 1].id : null
                        }
                      />
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            </div>
            // <div className="mt-[15vh] flex items-center justify-center">
            //   <div className="flex w-[520px] max-w-md flex-col rounded-md border p-10 shadow-md">
            //     <div className="text-xl font-medium leading-none">
            //       No Artifacts
            //     </div>
            //     <div className="py-7">
            //       <div className="text-left font-light leading-relaxed text-gray-500"></div>
            //       <div className="text-left font-light leading-relaxed text-gray-500">
            //         Begin by adding a
            //         <span className="font-normal text-gray-800">file</span> or{" "}
            //         <span className="font-normal text-gray-800">link</span>.
            //         <div className="mt-3 max-w-[340px]">
            //           If you have a want to group artifacts, you can create a{" "}
            //           <span className="font-normal text-gray-800">folder</span>{" "}
            //           first.
            //         </div>
            //         {/* Projects are made up of{" "}
            //         <span className="font-normal text-gray-800">stages</span>{" "}
            //         and stages are made up of{" "}
            //         <span className="font-normal text-gray-800">tasks</span>. */}
            //       </div>
            //     </div>

            //     <div className="flex space-x-2.5">
            //       <NewFolderButton
            //         phaseId={phaseId}
            //         parentFolder={
            //           tree.length > 1 ? tree[tree.length - 1].id : null
            //         }
            //       />
            //       <AddResourceButton phaseId={phaseId} tree={tree} />
            //     </div>
            //   </div>
            // </div>
            // <div className="relative mx-auto mt-[15vh] flex w-96 flex-col items-center p-8">
            //   <DocumentTextIcon className="h-7 w-7 text-gray-400" />
            //   <div className="mt-1 text-xl font-medium text-gray-700">
            //     Resources
            //   </div>
            //   <div className="mb-2 mt-1 max-w-[200px] text-center font-light text-gray-500">
            //     Resources enable easy access to files and links.
            //   </div>
            //   <div className="mt-3 flex items-center space-x-2">

            //   </div>
            // </div>
          )}
        </>
      ) : (
        <Loading className="mt-[20vh]" />
      )}
      <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
        {activeId ? (
          <div
            style={{
              width: "fit-content",
            }}
            className="cursor-grab rounded-md border-[0.5px] bg-white px-2.5 py-1 text-sm shadow"
          >
            {findItemById(resourcesWithFolders ?? [], activeId)?.name || ""}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function FolderDropdownMenuShell({
  folderId,
  resourcesWithFolders,
  phaseId,
  onDelete,
}: {
  folderId: string;
  resourcesWithFolders: ResourceWithFoldersData;
  phaseId: string;
  onDelete?: () => void;
}) {
  const folder = findItemById(resourcesWithFolders, folderId);
  if (!folderId) {
    return null;
  }
  if (!folder || folder.type !== "folder") {
    return null;
  }
  return (
    <FolderDropdownMenu folder={folder} phaseId={phaseId} onDelete={onDelete} />
  );
}

function FolderDropdownMenu({
  folder,
  phaseId,
  onDelete,
}: {
  folder: FolderData;
  phaseId: string;
  onDelete?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <>
      <DropdownMenu
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant={"ghost"}
            className={clsx(
              "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
              !open ? "bg-white" : "bg-gray-200/60"
            )}
          >
            <EllipsisHorizontalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-44" align="start">
          <DropdownMenuGroup>
            <DropdownMenuItem
              // className="py-1 focus:bg-gray-50 focus:text-red-600"
              onClick={(e) => {
                setIsEditing(true);
                e.stopPropagation();
              }}
              // className="py-1"
            >
              <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="ml-2 text-sm">Edit</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              // className="py-1 focus:bg-gray-50 focus:text-red-600"
              className="focus:bg-red-50 focus:text-red-600"
              onClick={(e) => {
                setIsDeleting(true);
                e.stopPropagation();
              }}
            >
              <TrashIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="ml-2 text-sm">Delete</span>
            </DropdownMenuItem>
            {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialogs
        folder={folder}
        phaseId={phaseId}
        setIsEditing={setIsEditing}
        setIsDeleting={setIsDeleting}
        isEditing={isEditing}
        isDeleting={isDeleting}
        onDelete={onDelete}
      />
    </>
  );
}

function Dialogs({
  folder,
  phaseId,
  setIsEditing,
  setIsDeleting,
  isEditing,
  isDeleting,
  onDelete,
}: {
  folder: FolderData;
  phaseId: string;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>;
  isEditing: boolean;
  isDeleting: boolean;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(folder.name);
  const [isVisible, setIsVisible] = useState(folder.isVisible);
  const updateFolderMutation = useUpdateFolder(phaseId);
  const deleteFolderMutation = useDeleteFolder(phaseId);
  const resourceCtx = useResourcesContext();

  useEffect(() => {
    if (folder) {
      setName(folder.name);
      setIsVisible(folder.isVisible);
    }
  }, [folder]);

  useEffect(() => {
    if (deleteFolderMutation.isSuccess) {
      setIsDeleting(false);
    }
  }, [deleteFolderMutation.isSuccess]);

  return (
    <>
      <Dialog
        open={isEditing}
        onOpenChange={(open) => {
          setIsEditing(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Folder</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-6">
              <div className="space-y-1">
                <InputHeading heading="Name" />
                <TextInput
                  className="w-72"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    if (name.trim() !== "" && name.trim() !== folder.name) {
                      updateFolderMutation.mutate({
                        _id: folder._id,
                        name: name.trim(),
                      });
                    }
                  }}
                />
              </div>
              {!resourceCtx.state.isExternal && (
                <div className="space-y-1">
                  <InputHeading heading="Visibility" />
                  <Select
                    value={isVisible ? "all" : "internal"}
                    onValueChange={(value) => {
                      setIsVisible(value === "all");
                      if (folder.isVisible !== (value === "all")) {
                        if (value === "all") {
                          updateFolderMutation.mutate({
                            _id: folder._id,
                            isVisible: true,
                          });
                        } else {
                          updateFolderMutation.mutate({
                            _id: folder._id,
                            isVisible: false,
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 w-fit">
                      {isVisible ? (
                        <div className="flex items-center space-x-2 pr-1">
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                          <div>Externally visible</div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 pr-1">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externally hidden</div>
                        </div>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="all">
                          <div className="flex items-center space-x-2">
                            <div>
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>Externally visible</div>
                          </div>
                        </SelectItem>
                        <SelectItem value={"internal"}>
                          <div className="flex items-center space-x-2">
                            <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                            <div>Externall hidden</div>
                          </div>
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Folder</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            {folder.children.length !== 0 ? (
              <div className="text-sm text-gray-600">
                You cannot delete a folder with other artifacts inside it.
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                Are you sure you want to delete the folder{" "}
                <span className="font-medium text-gray-900">{folder.name}</span>
                ?
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2.5">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsDeleting(false);
              }}
            >
              Cancel
            </Button>
            {folder.children.length === 0 && (
              <Button
                variant={"destructive"}
                disabled={deleteFolderMutation.isLoading}
                onClick={() => {
                  deleteFolderMutation.mutate({
                    _id: folder._id,
                  });
                  if (onDelete) {
                    onDelete();
                  }
                }}
              >
                {deleteFolderMutation.isLoading ? "Deleting.." : "Delete"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// function EditFolderDialogShell({
//   folderId,
//   resourcesWithFolders,
//   phaseId,
// }: {
//   folderId: string | null;
//   resourcesWithFolders: ResourceWithFoldersData;
//   phaseId: string;
// }) {
//   if (!folderId) {
//     return null;
//   }
//   const folder = findItemById(resourcesWithFolders, folderId);
//   if (!folder || folder.type !== "folder") {
//     return null;
//   }

//   return (
//     <EditFolderDialog
//       folder={folder}
//       resourcesWithFolders={resourcesWithFolders}
//       phaseId={phaseId}
//     />
//   );
// }

// function EditFolderDialog({
//   folder,
//   resourcesWithFolders,
//   phaseId,
// }: {
//   folder: FolderData;
//   resourcesWithFolders: ResourceWithFoldersData;
//   phaseId: string;
// }) {
//   const [name, setName] = useState(folder.name);
//   const [isVisible, setIsVisible] = useState(folder.isVisible);
//   const updateFolderMutation = useUpdateFolder(phaseId);
//   const [isDeleting, setIsDeleting] = useState(false);
//   useEffect(() => {
//     if (folder) {
//       setName(folder.name);
//       setIsVisible(folder.isVisible);
//     }
//   }, [folder]);
//   const resourceCtx = useResourcesContext();
//   return (

//   );
// }
const DraggableItem = ({
  item,
  children,
}: {
  item: Resource2Data;
  children: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: item._id.toString(),
    });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.5 : 1,
    // transform: transform
    //   ? `translate(${transform.x}px, ${transform.y}px)`
    //   : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

type DroppableProps = {
  folderId: string;
  hoverColor: string;
} & HTMLAttributes<HTMLDivElement>;

const DroppableFolder = forwardRef<HTMLDivElement, DroppableProps>(
  ({ children, folderId, hoverColor, ...props }, ref) => {
    //     ({
    //   folderId,
    //   children,
    // }: {
    //   folderId: string;
    //   children: React.ReactNode;
    // }) => {
    const { active } = useDndContext();
    const isActive = active?.id === folderId;

    const { isOver, setNodeRef } = useDroppable({
      id: folderId,
      disabled: isActive, // Disable droppable if dragging over itself
    });

    const style: React.CSSProperties = {
      backgroundColor: isOver ? hoverColor : undefined,
    };

    return (
      <div ref={setNodeRef} style={style} {...props}>
        {children}
      </div>
    );
  }
);

const findItemById = (
  items: Resource2Data[],
  id: string
): Resource2Data | null => {
  for (const item of items) {
    if (item._id.toString() === id) {
      return item;
    }
    if (item.type === "folder") {
      const found = findItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

interface ItemAndParent {
  parent: FolderData | null;
  item: Resource2Data | null;
}

const findItemAndParent = (
  items: Resource2Data[],
  id: string,
  parent: FolderData | null = null
): ItemAndParent => {
  for (const item of items) {
    if (item._id.toString() === id) {
      return { parent, item };
    }
    if (item.type === "folder") {
      const result = findItemAndParent(item.children, id, item);
      if (result.item) return result;
    }
  }
  return { parent: null, item: null };
};

function FormResourceRow({
  resource,
  isExternal,
}: {
  resource: FormResource2Data;
  isExternal: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <DraggableItem key={resource._id} item={resource}>
      <div
        className="flex h-12 cursor-pointer justify-between gap-6 border-b border-gray-100 px-8 hover:bg-gray-50"
        onClick={() => {
          const searchParams = new URLSearchParams(location.search);
          searchParams.set("taskId", resource.task._id);
          navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
          });
        }}
        key={resource.task._id}
      >
        <div className="flex min-w-0 flex-1 items-center space-x-4">
          <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
                <DocumentTextIcon className="h-[18px] w-[18px] text-gray-400" />
              </TooltipTrigger>
              <TooltipContent side="right">
                <div>Form</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="select-none text-sm font-medium text-gray-600">
            {resource.name}
          </div>
          {/* <div className="inline-flex w-10 items-center">
        </div>
        <div className="min-w-[160px] flex-1 truncate text-sm font-medium text-gray-600">
          
        </div> */}
        </div>
        <div className="flex shrink-0 items-center space-x-4">
          {!isExternal && (
            <div className="flex w-32 flex-col items-center">
              {resource.task.isVisible ? (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <EyeIcon className="h-[18px] w-[18px] text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <div>Externally visible</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <EyeSlashIcon className="h-[18px] w-[18px] text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <div>Externally hidden</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}

          <div className="w-40 truncate text-sm text-gray-400">{""}</div>
          <div className="w-32 text-sm text-gray-400">
            {/* {dayjs(resource.createdAt).format("MMM DD, h:mm A")} */}
          </div>
          <div className="w-16" />
          {/* <div className="w-20">{}</div> */}
        </div>
      </div>
    </DraggableItem>
  );
}

function FolderResourceRow({
  resource,
  isExternal,
  setTree,
  tree,
  phaseId,
}: {
  resource: FolderData;
  isExternal: boolean;
  setTree: React.Dispatch<React.SetStateAction<{ id: string; name: string }[]>>;
  tree: { id: string; name: string }[];
  phaseId: string;
}) {
  const [open, setOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <>
      <DraggableItem key={resource._id} item={resource}>
        <div
          className="flex h-12 cursor-pointer justify-between gap-6 border-b border-gray-100 px-8 hover:bg-gray-50"
          onClick={() => {
            setTree([...tree, { id: resource._id, name: resource.name }]);
          }}
          key={resource._id}
        >
          <div className="flex min-w-0 flex-1 items-center space-x-4">
            <FolderIcon className="h-[18px] w-[18px] text-gray-400" />
            {/* <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div>Folder</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> */}
            <div className="flex items-center space-x-3">
              <div className="select-none text-sm font-medium text-gray-600">
                {resource.name}
              </div>
              <Count
                className={clsx(resource.children.length === 0 && "opacity-30")}
              >
                {resource.children.length}
              </Count>
            </div>
            {/* <div className="inline-flex w-10 items-center">
          </div>
          <div className="min-w-[160px] flex-1 truncate text-sm font-medium text-gray-600">
            
          </div> */}
          </div>
          <div className="flex shrink-0 items-center space-x-4">
            {!isExternal && (
              <div className="flex w-32 flex-col items-center">
                {resource.isVisible ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <EyeIcon className="h-[18px] w-[18px] text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>Externally visible</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <EyeSlashIcon className="h-[18px] w-[18px] text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>Externally hidden</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}

            <div className="w-40 truncate text-sm text-gray-500">
              {resource?.createdBy ? resource.createdBy.name : "Sero"}
            </div>
            <div className="w-32 text-sm text-gray-500">
              {dayjs(resource.createdAt).format("MMM DD, h:mm A")}
            </div>
            <div className="w-16">
              <DropdownMenu
                open={optionsOpen}
                onOpenChange={(isOpen) => {
                  setOptionsOpen(isOpen);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={"ghost"}
                    className={clsx(
                      "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                      !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
                    )}
                  >
                    <EllipsisHorizontalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44" align="end">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsEditing(true);
                        e.stopPropagation();
                      }}
                      // className="py-1"
                    >
                      <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Edit</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem>
                    {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* <div className="w-20">{}</div> */}
          </div>
        </div>
      </DraggableItem>
      <Dialogs
        folder={resource}
        phaseId={phaseId}
        setIsEditing={setIsEditing}
        setIsDeleting={setIsDeleting}
        isEditing={isEditing}
        isDeleting={isDeleting}
      />
    </>
  );
}

function FileResourceRow({
  resource,
  isExternal,
  phaseTasks,
}: {
  resource: FileResource2Data;
  isExternal: boolean;
  phaseTasks: TaskData[];
}) {
  const [open, setOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [name, setName] = useState(resource.name);
  const [description, setDescription] = useState(resource.description);
  const updateResourceMutation = useUpdatePhaseResource(resource.phaseId);
  const deleteResourceMutation = useDeleteResource(resource.phaseId);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const resourceCtx = useResourcesContext();
  const [visibility, setVisibility] = useState<"internal" | "all">(
    resource.isVisible ? "all" : "internal"
  );

  useEffect(() => {
    if (deleteResourceMutation.isSuccess) {
      setIsDeleting(false);
    }
  }, [deleteResourceMutation.isSuccess]);

  const task = useMemo(() => {
    return phaseTasks.find(
      (task) => task._id?.toString() === resource.taskId?.toString()
    );
  }, [phaseTasks]);

  return (
    <>
      <DraggableItem key={resource._id} item={resource}>
        <div
          className="flex h-12 cursor-pointer justify-between gap-6 border-b border-gray-100 px-8 hover:bg-gray-50"
          onClick={() => {
            setOpen(true);
          }}
          key={resource._id}
        >
          <div className="flex min-w-0 flex-1 items-center space-x-4">
            <PaperClipIcon className="h-[18px] w-[18px] shrink-0 text-gray-400" />
            {/* <TooltipProvider>
            <Tooltip delayDuration={0}>
              <TooltipTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <div>File</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> */}
            <div className="min-w-0 select-none truncate text-sm font-medium text-gray-600">
              {resource.name}
            </div>
            {!!task && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger>
                    <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-[0.5px] border-primary bg-white">
                      <BoltIcon className="h-3.5 w-3.5 text-primary" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent sideOffset={0} side="right">
                    <span>File uploaded in task</span>{" "}
                    <span className="max-w-[160px] truncate font-normal underline">
                      {task.title}
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* <div className="inline-flex w-10 items-center">
          </div>
          <div className="min-w-[160px] flex-1 truncate text-sm font-medium text-gray-600">
            
          </div> */}
          </div>
          <div className="flex shrink-0 items-center space-x-4">
            {!isExternal && (
              <div className="flex w-32 flex-col items-center">
                {resource.isVisible ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <EyeIcon className="h-[18px] w-[18px] text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>Externally visible</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <EyeSlashIcon className="h-[18px] w-[18px] text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>Externally hidden</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}

            <div className="w-40 truncate text-sm text-gray-500">
              {resource.createdBy?.name}
            </div>
            <div className="w-32 text-sm text-gray-500">
              {dayjs(resource.createdAt).format("MMM DD, h:mm A")}
            </div>
            <div className="w-16">
              <DropdownMenu
                open={optionsOpen}
                onOpenChange={(isOpen) => {
                  setOptionsOpen(isOpen);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={"ghost"}
                    className={clsx(
                      "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                      !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
                    )}
                  >
                    <EllipsisHorizontalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44" align="end">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsEditing(true);
                        e.stopPropagation();
                      }}
                      // className="py-1"
                    >
                      <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Edit</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem>
                    {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* <div className="w-20">{}</div> */}
          </div>
        </div>
      </DraggableItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{resource.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {resource.description.trim() !== "" && (
              <div className="text-sm text-gray-600">
                {resource.description}
              </div>
            )}
            <Button
              variant={"link"}
              className="px-0"
              onClick={() => {
                window.open(resource.file.url, "_blank");
              }}
            >
              Open File
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit File</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-1">
              <InputHeading heading="Name" />
              <TextInput
                className="w-72"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                onBlur={() => {
                  if (name.trim() === "") {
                    setName(resource.name);
                  } else if (name.trim() !== resource.name.trim()) {
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      name: name.trim(),
                    });
                  }
                }}
                placeholder="Resource name..."
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="Description" />
              <textarea
                className="block w-full resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                ref={textAreaRef}
                placeholder="Description..."
                onBlur={() => {
                  if (description.trim() === "") {
                    setDescription(resource.description);
                  } else if (
                    description.trim() !== resource.description.trim()
                  ) {
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      description: description.trim(),
                    });
                  }
                }}
              />
            </div>
            {!resourceCtx.state.isExternal && (
              <div className="space-y-1">
                <InputHeading heading="Visibility" />
                <Select
                  value={visibility}
                  onValueChange={(value) => {
                    setVisibility(value as typeof visibility);
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      isVisible: value === "all",
                    });
                  }}
                >
                  <SelectTrigger className="h-9 w-fit">
                    {visibility === "all" ? (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally visible</div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally hidden</div>
                      </div>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        <div className="flex items-center space-x-2">
                          <div>
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                          </div>
                          <div>Externally visible</div>
                        </div>
                      </SelectItem>
                      <SelectItem value={"internal"}>
                        <div className="flex items-center space-x-2">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externall hidden</div>
                        </div>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="text-sm text-gray-600">
              Are you sure you want to delete the file{" "}
              <span className="font-medium text-gray-900">{resource.name}</span>
              ?
              {resource.taskId && (
                <>
                  <br />
                  <br />
                  This file is attached to{" "}
                  {!!task ? (
                    <>
                      <span>the task</span>{" "}
                      <span className="font-medium text-gray-900">
                        {task?.title}.
                      </span>
                    </>
                  ) : (
                    "a task."
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex justify-end space-x-2.5">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsDeleting(false);
              }}
            >
              Cancel
            </Button>

            <Button
              variant={"destructive"}
              disabled={deleteResourceMutation.isLoading}
              onClick={() => {
                deleteResourceMutation.mutate({
                  _id: resource._id,
                });
              }}
            >
              {deleteResourceMutation.isLoading ? "Deleting.." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LinkResourceRow({
  resource,
  isExternal,
}: {
  resource: LinkResource2Data;
  isExternal: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [name, setName] = useState(resource.name);
  const [description, setDescription] = useState(resource.description);
  const updateResourceMutation = useUpdatePhaseResource(resource.phaseId);
  const deleteResourceMutation = useDeleteResource(resource.phaseId);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const resourceCtx = useResourcesContext();
  const [visibility, setVisibility] = useState<"internal" | "all">(
    resource.isVisible ? "all" : "internal"
  );
  const [error, setError] = useState("");
  const [url, setUrl] = useState(resource.link.url);

  useEffect(() => {
    if (deleteResourceMutation.isSuccess) {
      setIsDeleting(false);
    }
  }, [deleteResourceMutation.isSuccess]);

  return (
    <>
      <DraggableItem key={resource._id} item={resource}>
        <div
          className="flex h-12 cursor-pointer justify-between gap-6 border-b border-gray-100 px-8 hover:bg-gray-50"
          onClick={() => {
            setOpen(true);
          }}
          key={resource._id}
        >
          <div className="flex min-w-0 flex-1 items-center space-x-4">
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger>
                  <Link2Icon className="h-[18px] w-[18px] text-gray-400" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div>Link</div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="select-none text-sm font-medium text-gray-600">
              {resource.name}
            </div>
          </div>
          <div className="flex shrink-0 items-center space-x-4">
            {!isExternal && (
              <div className="flex w-32 flex-col items-center">
                {resource.isVisible ? (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <EyeIcon className="h-[18px] w-[18px] text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>Externally visible</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger>
                        <EyeSlashIcon className="h-[18px] w-[18px] text-gray-400" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>Externally hidden</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}

            <div className="w-40 truncate text-sm text-gray-500">
              {resource.createdBy?.name}
            </div>
            <div className="w-32 text-sm text-gray-500">
              {dayjs(resource.createdAt).format("MMM DD, h:mm A")}
            </div>
            <div className="w-16">
              <DropdownMenu
                open={optionsOpen}
                onOpenChange={(isOpen) => {
                  setOptionsOpen(isOpen);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={"ghost"}
                    className={clsx(
                      "h-6 w-6 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                      !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
                    )}
                  >
                    <EllipsisHorizontalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44" align="end">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsEditing(true);
                        e.stopPropagation();
                      }}
                      // className="py-1"
                    >
                      <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Edit</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem>
                    {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* <div className="w-20">{}</div> */}
          </div>
        </div>
      </DraggableItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{resource.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {resource.description.trim() !== "" && (
              <div className="text-sm text-gray-600">
                {resource.description}
              </div>
            )}
            <Button
              variant={"link"}
              className="px-0"
              onClick={() => {
                window.open(resource.link.url, "_blank");
              }}
            >
              Open Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-1">
              <InputHeading heading="Name" />
              <TextInput
                className="w-72"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                onBlur={() => {
                  if (name.trim() === "") {
                    setName(resource.name);
                  } else if (name.trim() !== resource.name.trim()) {
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      name: name.trim(),
                    });
                  }
                }}
                placeholder="Resource name..."
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="Description" />
              <textarea
                className="block w-full resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                ref={textAreaRef}
                placeholder="Description..."
                onBlur={() => {
                  if (description.trim() === "") {
                    setDescription(resource.description);
                  } else if (
                    description.trim() !== resource.description.trim()
                  ) {
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      description: description.trim(),
                    });
                  }
                }}
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="URL" />
              <TextInput
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError("");
                }}
                onBlur={() => {
                  const urlRegex = /^(http|https):\/\/[^ "]+$/;
                  if (!urlRegex.test(url)) {
                    setError("URL must start with http:// or https://");
                  } else if (url.trim() !== resource.link.url.trim()) {
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      url: url,
                      name: name,
                      description: description,
                      isVisible: visibility === "all",
                    });
                  }
                }}
              />
              {error && <div className="text-xs text-red-500">{error}</div>}
            </div>
            {!resourceCtx.state.isExternal && (
              <div className="space-y-1">
                <InputHeading heading="Visibility" />
                <Select
                  value={visibility}
                  onValueChange={(value) => {
                    setVisibility(value as typeof visibility);
                    updateResourceMutation.mutate({
                      _id: resource._id,
                      isVisible: value === "all",
                    });
                  }}
                >
                  <SelectTrigger className="h-9 w-fit">
                    {visibility === "all" ? (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally visible</div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally hidden</div>
                      </div>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        <div className="flex items-center space-x-2">
                          <div>
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                          </div>
                          <div>Externally visible</div>
                        </div>
                      </SelectItem>
                      <SelectItem value={"internal"}>
                        <div className="flex items-center space-x-2">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externall hidden</div>
                        </div>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Link</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="text-sm text-gray-600">
              Are you sure you want to delete the link{" "}
              <span className="font-medium text-gray-900">{resource.name}</span>
              ?
            </div>
          </div>
          <div className="flex justify-end space-x-2.5">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsDeleting(false);
              }}
            >
              Cancel
            </Button>

            <Button
              variant={"destructive"}
              disabled={deleteResourceMutation.isLoading}
              onClick={() => {
                deleteResourceMutation.mutate({
                  _id: resource._id,
                });
              }}
            >
              {deleteResourceMutation.isLoading ? "Deleting.." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LinkResourceDetails({ resource }: { resource: LinkResource2Data }) {
  const [isEditing, setIsEditing] = useState(false);
  const [url, setUrl] = useState(resource.link.url);
  const [name, setName] = useState(resource.name);
  const [description, setDescription] = useState(resource.description);
  const [error, setError] = useState("");
  const updateResourceMutation = useUpdatePhaseResource(resource.phaseId);
  const deleteResourceMutation = useDeleteResource(resource.phaseId);
  const [isDeleting, setIsDeleting] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const resourceCtx = useResourcesContext();
  const [visibility, setVisibility] = useState<"internal" | "all">(
    resource.isVisible ? "all" : "internal"
  );

  useEffect(() => {
    if (updateResourceMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateResourceMutation.isSuccess]);

  return (
    <>
      {!isEditing ? (
        <div>
          <div className="space-y-2">
            <div className="text-xl font-medium">{resource.name}</div>
            <div className="text-gray-500">{resource.description}</div>
            <div>
              <Button
                onClick={() => {
                  window.open(resource.link.url, "_blank");
                }}
                variant={"link"}
                className="px-0"
              >
                Open Link
              </Button>
            </div>
          </div>
          <div className="mt-6 flex items-center space-x-2">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-xl font-medium">Editing Link</div>
          <div className="space-y-4">
            <div className="space-y-1">
              <InputHeading heading="Name" />
              <TextInput
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="Resource name..."
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="Description" />
              <textarea
                className="block w-full resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                ref={textAreaRef}
                placeholder="Description..."
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="URL" />
              <TextInput
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError("");
                }}
              />
              {error && <div className="text-xs text-red-500">{error}</div>}
            </div>
            {!resourceCtx.state.isExternal && (
              <div className="space-y-1">
                <InputHeading heading="Visibility" />
                <Select
                  value={visibility}
                  onValueChange={(value) => {
                    setVisibility(value as typeof visibility);
                  }}
                >
                  <SelectTrigger className="h-9 w-fit">
                    {visibility === "all" ? (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally visible</div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally hidden</div>
                      </div>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        <div className="flex items-center space-x-2">
                          <div>
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                          </div>
                          <div>Externally visible</div>
                        </div>
                      </SelectItem>
                      <SelectItem value={"internal"}>
                        <div className="flex items-center space-x-2">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externall hidden</div>
                        </div>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
              <DialogTrigger asChild>
                <Button variant={"secondary"} size="icon">
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Resource</DialogTitle>
                </DialogHeader>
                <div className="py-2">
                  <div className="text-sm text-gray-600">
                    Are you sure you want to delete the resource{" "}
                    <span className="font-medium text-gray-900">
                      {resource.name}
                    </span>
                    ?
                  </div>
                </div>
                <div className="flex justify-end space-x-2.5">
                  <Button
                    variant={"secondary"}
                    onClick={() => {
                      setIsDeleting(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant={"destructive"}
                    onClick={() => {
                      setIsDeleting(false);
                      setIsEditing(false);
                      deleteResourceMutation.mutate({
                        _id: resource._id,
                      });
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <div className="flex items-center space-x-2.5">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>

              <Button
                disabled={
                  url.trim() === "" ||
                  name.trim() === "" ||
                  (url.trim() === resource.link.url.trim() &&
                    name.trim() === resource.name.trim() &&
                    description.trim() === resource.description.trim() &&
                    visibility === (resource.isVisible ? "all" : "internal")) ||
                  updateResourceMutation.isLoading
                }
                onClick={() => {
                  if (
                    url.trim() !== resource.link.url.trim() ||
                    name.trim() !== resource.name.trim() ||
                    description.trim() !== resource.description.trim() ||
                    visibility !== (resource.isVisible ? "all" : "internal")
                  ) {
                    const urlRegex = /^(http|https):\/\/[^ "]+$/;
                    if (!urlRegex.test(url)) {
                      setError("URL must start with http:// or https://");
                    } else {
                      updateResourceMutation.mutate({
                        _id: resource._id,
                        url: url,
                        name: name,
                        description: description,
                        isVisible: visibility === "all",
                      });
                    }
                  }
                }}
              >
                {updateResourceMutation.isLoading ? "Saving.." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FileResourceDetails({ resource }: { resource: FileResource2Data }) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(resource.name);
  const [description, setDescription] = useState(resource.description);
  const updateResourceMutation = useUpdatePhaseResource(resource.phaseId);
  const deleteResourceMutation = useDeleteResource(resource.phaseId);
  const [isDeleting, setIsDeleting] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const resourceCtx = useResourcesContext();
  const [visibility, setVisibility] = useState<"internal" | "all">(
    resource.isVisible ? "all" : "internal"
  );

  useEffect(() => {
    if (updateResourceMutation.isSuccess) {
      setIsEditing(false);
    }
  }, [updateResourceMutation.isSuccess]);

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto"; // Reset the height
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`; // Set the height based on scroll height
    }
  }, [description, isEditing]);

  return (
    <>
      {!isEditing ? (
        <div>
          <div className="space-y-2">
            <div className="text-xl font-medium">{resource.name}</div>
            <div className="text-gray-500">{resource.description}</div>
            <div>
              <Button
                onClick={() => {
                  window.open(resource.file.url, "_blank");
                }}
                variant={"link"}
                className="px-0"
              >
                Open File
              </Button>
            </div>
          </div>
          <div className="mt-6 flex items-center space-x-2">
            <Button
              variant={"secondary"}
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="text-xl font-medium">Editing File</div>
          <div className="space-y-4">
            <div className="space-y-1">
              <InputHeading heading="Name" />
              <TextInput
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="Resource name..."
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="Description" />
              <textarea
                className="block w-full resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                ref={textAreaRef}
                placeholder="Description..."
              />
            </div>
            {!resourceCtx.state.isExternal && (
              <div className="space-y-1">
                <InputHeading heading="Visibility" />
                <Select
                  value={visibility}
                  onValueChange={(value) => {
                    setVisibility(value as typeof visibility);
                  }}
                >
                  <SelectTrigger className="h-9 w-fit">
                    {visibility === "all" ? (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally visible</div>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 pr-1">
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally hidden</div>
                      </div>
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">
                        <div className="flex items-center space-x-2">
                          <div>
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                          </div>
                          <div>Externally visible</div>
                        </div>
                      </SelectItem>
                      <SelectItem value={"internal"}>
                        <div className="flex items-center space-x-2">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externall hidden</div>
                        </div>
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-between">
            <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
              <DialogTrigger asChild>
                <Button variant={"secondary"} size="icon">
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Resource</DialogTitle>
                </DialogHeader>
                <div className="py-2">
                  <div className="text-sm text-gray-600">
                    Are you sure you want to delete the resource{" "}
                    <span className="font-medium text-gray-900">
                      {resource.name}
                    </span>
                    ?
                  </div>
                </div>
                <div className="flex justify-end space-x-2.5">
                  <Button
                    variant={"secondary"}
                    onClick={() => {
                      setIsDeleting(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant={"destructive"}
                    onClick={() => {
                      setIsDeleting(false);
                      setIsEditing(false);
                      deleteResourceMutation.mutate({
                        _id: resource._id,
                      });
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <div className="flex items-center space-x-2.5">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  name.trim() === "" ||
                  (name.trim() === resource.name.trim() &&
                    description.trim() === resource.description.trim() &&
                    visibility === (resource.isVisible ? "all" : "internal")) ||
                  updateResourceMutation.isLoading
                }
                onClick={() => {
                  updateResourceMutation.mutate({
                    _id: resource._id,
                    name: name,
                    description: description,
                    isVisible: visibility === "all",
                  });
                }}
              >
                {updateResourceMutation.isLoading ? "Saving.." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NewFolderButton({
  phaseId,
  parentFolder,
}: {
  phaseId: string;
  parentFolder: string | null;
}) {
  const resourceCtx = useResourcesContext();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState<"internal" | "all">("all");
  const createFolderMutation = useCreateResourceFolder(phaseId);
  useEffect(() => {
    if (createFolderMutation.isSuccess) {
      setOpen(false);
      setName("");
    }
  }, [createFolderMutation.isSuccess]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>New Folder</Button>
      </DialogTrigger>
      <DialogContent className="top-[12%] translate-y-0">
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="space-y-1">
            <InputHeading heading="Name" />
            <TextInput
              autoComplete="off"
              placeholder="Folder Name"
              value={name}
              className="w-72"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {!resourceCtx.state.isExternal && (
            <div className="space-y-1">
              <InputHeading heading="Visibility" />
              <Select
                value={visibility}
                onValueChange={(value) => {
                  setVisibility(value as typeof visibility);
                }}
              >
                <SelectTrigger className="h-9 w-fit">
                  {visibility === "all" ? (
                    <div className="flex items-center space-x-2 pr-1">
                      <EyeIcon className="h-4 w-4 text-gray-400" />
                      <div>Externally visible</div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 pr-1">
                      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                      <div>Externally hidden</div>
                    </div>
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">
                      <div className="flex items-center space-x-2">
                        <div>
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <div>Externally visible</div>
                      </div>
                    </SelectItem>
                    <SelectItem value={"internal"}>
                      <div className="flex items-center space-x-2">
                        <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                        <div>Externally hidden</div>
                      </div>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!name.trim() || createFolderMutation.isLoading}
            onClick={() => {
              createFolderMutation.mutate({
                name: name,
                isVisible: visibility === "all",
                ...(parentFolder ? { parentFolderId: parentFolder } : {}),
              });
            }}
          >
            {createFolderMutation.isLoading ? "Creating..." : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddResourceButton({
  phaseId,
  tree,
}: {
  phaseId: string;
  tree: { id: string; name: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [resourceType, setResourceType] = useState<"file" | "link" | "none">(
    "none"
  );
  useEffect(() => {
    if (open) {
      setResourceType("none");
      setLabel("");
      setFile(undefined);
      setVisibility("all");
      setUrl("");
      setUrlError("");
      setDescription("");
    }
  }, [open]);

  const resourceCtx = useResourcesContext();

  const [urlError, setUrlError] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [url, setUrl] = useState("");
  const [visibility, setVisibility] = useState<"internal" | "all">("all");
  const uploadPhaseFileMutation = useUploadPhaseFile();
  const createPhaseLinkMutation = useCreatePhaseLinkResource(phaseId);
  useEffect(() => {
    if (uploadPhaseFileMutation.isSuccess) {
      setOpen(false);
    }
  }, [uploadPhaseFileMutation.isSuccess]);

  useEffect(() => {
    if (createPhaseLinkMutation.isSuccess) {
      setOpen(false);
    }
  }, [createPhaseLinkMutation.isSuccess]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto"; // Reset the height
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`; // Set the height based on scroll height
    }
  }, [description]);

  const getS3UploadLink = useGetUploadLinksForFiles();
  const uploadFileToS3 = useUploadFileToS3();
  const app = useRealmApp();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>Add Artifact</Button>
      </DialogTrigger>
      <DialogContent className="top-[12%] translate-y-0">
        <DialogHeader>
          <DialogTitle>
            {resourceType === "none"
              ? "New Artifact"
              : resourceType === "file"
              ? "New File"
              : "New Link"}
          </DialogTitle>
          {resourceType === "none" && (
            <DialogDescription>
              {resourceType === "none"
                ? "Select a type of artifact to share"
                : resourceType === "file"
                ? "Details about the file you want to share"
                : "Details about the link you want to share"}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="pt-2">
          {resourceType === "none" && (
            <div>
              {/* <div>What type of resource would you like to add?</div> */}
              <div className="grid gap-3">
                <div
                  className={
                    "cursor-pointer rounded-md border py-3 px-4 shadow-sm hover:bg-gray-50"
                  }
                  onClick={() => setResourceType("file")}
                >
                  <div className="flex items-center space-x-1.5">
                    <PaperClipIcon className="h-[18px] w-[18px] text-gray-400" />
                    <div className="text-lg font-medium">File</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      Upload PDFs, images and other types of files
                    </div>
                  </div>
                </div>
                <div
                  className={
                    "cursor-pointer rounded-md border py-3 px-4 shadow-sm hover:bg-gray-50"
                  }
                  onClick={() => setResourceType("link")}
                >
                  <div className="flex items-center space-x-2">
                    <Link2Icon className="h-[18px] w-[18px] text-gray-400" />
                    <div className="text-lg font-medium">Link</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      Share URLs of other websites
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {resourceType === "file" && (
            <div className="space-y-4">
              <div className="max-w-xs">
                <div className="space-y-1">
                  <InputHeading heading="Name" />
                  <TextInput
                    autoComplete="off"
                    placeholder="Artifact Name.."
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <InputHeading heading="Description" />
                <textarea
                  ref={textAreaRef}
                  className="block w-full resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  placeholder="Description..."
                />
              </div>
              <div className="space-y-1">
                <InputHeading heading="File" />
                <div className="flex flex-col items-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                  <div className="min-w-0 space-y-1 text-center">
                    {!!file ? (
                      <div className="max-w-xs truncate whitespace-nowrap text-lg font-medium text-primary">
                        {file.name}
                      </div>
                    ) : (
                      <DocumentTextIcon className="mx-auto mb-2 h-6 w-6 text-gray-400" />
                    )}
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className={clsx(
                          "relative flex w-full cursor-pointer flex-col items-center rounded-md bg-white text-center font-medium transition-colors focus-within:outline-none",
                          !!file
                            ? "text-gray-400 hover:text-gray-500"
                            : "text-primary-600 hover:text-primary"
                        )}
                      >
                        <div>{!!file ? "Replace file" : "Select a file"}</div>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept="application/pdf, image/jpg, image/jpeg, image/png, image/svg, image/gif, .doc, .docx, .ppt, .pptx, .xls, .xlsx, .csv, .txt"
                          onChange={(e) => {
                            // console.log(e.target.files);
                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                              if (
                                !/^[a-zA-Z0-9_ -]+\.[a-zA-Z0-9]+$/.test(
                                  selectedFile.name
                                )
                              ) {
                                alert(
                                  "The file name must consist solely of letters, numbers, underscores, hyphens, and a single dot. Please ensure it adheres to this format."
                                );
                              } else if (selectedFile.size > 30 * 1024 * 1024) {
                                alert("File size should not exceed 30MB.");
                              } else {
                                setFile(selectedFile);
                              }
                            }
                          }}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      PDF, JPG, etc. up to 30MB
                    </p>
                  </div>
                </div>
              </div>
              {!resourceCtx.state.isExternal && (
                <div className="space-y-1">
                  <InputHeading heading="Visibility" />
                  <Select
                    value={visibility}
                    onValueChange={(value) => {
                      setVisibility(value as typeof visibility);
                    }}
                  >
                    <SelectTrigger className="h-9 w-fit">
                      {visibility === "all" ? (
                        <div className="flex items-center space-x-2 pr-1">
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                          <div>Externally visible</div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 pr-1">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externally hidden</div>
                        </div>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="all">
                          <div className="flex items-center space-x-2">
                            <div>
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>Externally visible</div>
                          </div>
                        </SelectItem>
                        <SelectItem value={"internal"}>
                          <div className="flex items-center space-x-2">
                            <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                            <div>Externall hidden</div>
                          </div>
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* {!filesCtx.state.isExternal && (
                  <div className="space-y-2">
                    <InputHeading heading="External Visibility" />
                    <div className="inline-flex items-center space-x-2">
                      <VariantToggle
                        focus={false}
                        enabled={isVisible}
                        setEnabled={(value) => {
                          setIsVisible(value);
                        }}
                        color={filesCtx.state.isExternal ? "embed" : "primary"}
                        size="md"
                      />
                      <div className="whitespace-nowrap text-sm font-light text-gray-400">
                        Visible
                      </div>
                    </div>
                  </div>
                )} */}
              <div className="flex justify-end">
                <Button
                  disabled={
                    !file ||
                    label.trim() === "" ||
                    getS3UploadLink.isLoading ||
                    uploadFileToS3.isLoading
                  }
                  onClick={async () => {
                    if (!file) return;

                    try {
                      // console.log({
                      //   createdBy: app.currentUser?.id,
                      //   fileName: file.name,
                      //   isVisible: visibility === "all",
                      //   description: description,
                      //   label: label,
                      //   phaseId: phaseId,
                      //   fileType: file.type,
                      //   instanceId: app.currentUser.customData.instanceId.$oid,
                      //   ...(tree.length > 1
                      //     ? { parentFolderId: tree[tree.length - 1].id }
                      //     : {}),
                      // });

                      const id = new ObjectID();
                      // console.log(id.toString());

                      const uploadUrl = await getS3UploadLink.mutateAsync([
                        {
                          id: id.toString(),
                          createdBy: app.currentUser?.id,
                          fileName: file.name,
                          isVisible: visibility === "all",
                          description: description,
                          label: label,
                          phaseId: phaseId,
                          fileType: file.type,
                          // instanceId:
                          //   app.currentUser.customData.instanceId.$oid,
                          ...(tree.length > 1
                            ? { parentFolderId: tree[tree.length - 1].id }
                            : {}),
                        },
                      ]);
                      // console.log(uploadUrl);
                      if (uploadUrl.length > 0) {
                        await uploadFileToS3.mutateAsync({
                          contentType: file.type,
                          file,
                          url: uploadUrl[0].url,
                        });
                        setOpen(false);
                        setFile(undefined);
                        setLabel("");
                        setDescription("");
                        setVisibility("all");
                      }
                    } catch (error) {
                      // Error handling is done in the mutation callbacks
                    }
                    // uploadPhaseFileMutation.mutate({
                    //   label: label,
                    //   description: description,
                    //   phaseId: phaseId,
                    //   isVisible: visibility === "all",
                    //   file: file,
                    //   ...(tree.length > 1
                    //     ? { parentFolderId: tree[tree.length - 1].id }
                    //     : {}),
                    //   // type: file?.type,
                    //   // encodedFile: base64String,
                    // });

                    // const reader = new FileReader();
                    // reader.readAsDataURL(file);
                    // reader.onload = () => {
                    //   const dataUrl = reader.result as string;
                    //   const match = dataUrl.match(/^data:.+\/(.+);base64,(.*)$/);

                    //   if (match) {
                    //     const contentType = match[1];
                    //     const base64String = match[2];

                    //   }
                    // };
                  }}
                >
                  {uploadFileToS3.isLoading || getS3UploadLink.isLoading
                    ? "Uploading..."
                    : "Upload"}
                </Button>
              </div>
            </div>
          )}
          {resourceType === "link" && (
            <div className="space-y-4">
              <div className="max-w-xs space-y-1">
                <InputHeading heading="Name" />
                <TextInput
                  autoComplete="off"
                  placeholder="Artifact Name.."
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <InputHeading heading="Description" />
                <textarea
                  ref={textAreaRef}
                  className="block w-full resize-none rounded-md border-input p-2.5 py-2 text-sm shadow-sm outline-none transition-all placeholder:text-gray-300 focus:border-primary focus:outline-none focus:ring-[0.5px] focus:ring-primary"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  placeholder="Description..."
                />
              </div>
              <div className="max-w-xs space-y-1">
                <InputHeading heading="URL" />
                <TextInput
                  autoComplete="off"
                  placeholder="https://www.youtube.com/123"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setUrlError("");
                  }}
                />
                {urlError && (
                  <div className="text-xs text-red-500">{urlError}</div>
                )}
              </div>

              {!resourceCtx.state.isExternal && (
                <div className="space-y-1">
                  <InputHeading heading="Visibility" />
                  <Select
                    value={visibility}
                    onValueChange={(value) => {
                      setVisibility(value as typeof visibility);
                    }}
                  >
                    <SelectTrigger className="h-9 w-fit">
                      {visibility === "all" ? (
                        <div className="flex items-center space-x-2 pr-1">
                          <EyeIcon className="h-4 w-4 text-gray-400" />
                          <div>Externally visible</div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 pr-1">
                          <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                          <div>Externally hidden</div>
                        </div>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="all">
                          <div className="flex items-center space-x-2">
                            <div>
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>Externally visible</div>
                          </div>
                        </SelectItem>
                        <SelectItem value={"internal"}>
                          <div className="flex items-center space-x-2">
                            <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                            <div>Externally hidden</div>
                          </div>
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* {!filesCtx.state.isExternal && (
                <div className="space-y-2">
                  <InputHeading heading="External Visibility" />
                  <div className="inline-flex items-center space-x-2">
                    <VariantToggle
                      focus={false}
                      enabled={isVisible}
                      setEnabled={(value) => {
                        setIsVisible(value);
                      }}
                      color={filesCtx.state.isExternal ? "embed" : "primary"}
                      size="md"
                    />
                    <div className="whitespace-nowrap text-sm font-light text-gray-400">
                      Visible
                    </div>
                  </div>
                </div>
              )} */}
              <div className="flex justify-end">
                <Button
                  disabled={
                    label.trim() === "" ||
                    url.trim() === "" ||
                    createPhaseLinkMutation.isLoading
                  }
                  onClick={() => {
                    // check if url starts with http or https
                    const urlRegex = /^(http|https):\/\/[^ "]+$/;
                    if (!urlRegex.test(url)) {
                      setUrlError("URL must start with http:// or https://");
                      return;
                    }
                    if (label.trim() === "" || url.trim() === "") return;
                    createPhaseLinkMutation.mutate({
                      name: label,
                      isVisible: visibility === "all",
                      url: url,
                      description: description,
                      ...(tree.length > 1
                        ? { parentFolderId: tree[tree.length - 1].id }
                        : {}),
                    });
                  }}
                >
                  {createPhaseLinkMutation.isLoading ? "Adding.." : "Add"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
